import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';
import qs from 'qs';
import type { InjectedRouter } from 'react-router';

import useFetchProjectsAction from './useFetchProjectsAction';
import * as ProjectsV2Store from '../../../../redux/projectsV2';
import { useDebouncedCallback } from '../../../../hooks';
import { parseQueryParams } from '../../../../helpers/projectsV2';
import type { HydrateStateQuery } from '../../../../redux/projectsV2/types';
import type { RouterProps } from '../../../../types';
import type { ProjectCategory } from '../../../../../shared/projectsV2';

type UseSyncStateAndURLParamsProps = RouterProps & {
  router: InjectedRouter;
};

/**
 * Takes the location.search and parses it out, so it can be
 * synced with the Redux state.
 *
 * @param {string} search - The router's location.search
 */
const parseSearchParams = (search: string) => {
  const parsed = qs.parse(search, {
    // `search` usually starts with a "?", so this allows us to ignore that.
    ignoreQueryPrefix: true,
  });
  return parseQueryParams(parsed);
};

/**
 * Takes the query, omits defaults and things which don't go in the
 * query params, stringifies them.
 *
 * Omit the project category, which is not needed in the URL,
 * as it is part of the location.pathanme param. If title is undefined/empty,
 * we don't want it to be added to the URL params.
 *
 * For all other params, only include them if they differ from their default.
 *
 * @param {HydrateStateQuery} query - The hydrate state query.
 */
const stringifyQuery = (query: HydrateStateQuery): string => {
  const initialState = ProjectsV2Store.reducer.initialState;

  // build up URL query with non-default / non-empty values
  const urlQuery: Partial<HydrateStateQuery> = {
    title: query.title ? query.title : undefined,
    sort: query.sort !== initialState.sort ? query.sort : undefined,
    page: query.page !== initialState.page ? query.page : undefined,
    count: query.count !== initialState.count ? query.count : undefined,
    filter: query.filter !== initialState.filter ? _.omitBy(query.filter, _.isUndefined) : undefined,
  };

  return qs.stringify(urlQuery);
};

/**
 * Hook which keeps the Redux state and the URL params in sync.
 * Does this by running one of two effects depending on if the
 * Redux state was updated or if the URL/router location has been updated.
 *
 * @param {InjectedRouter} router - React router.
 * @param {RouterProps["location"]} location - The history location object.
 */
const useSyncStateAndURLParams = ({ router, location }: UseSyncStateAndURLParamsProps): void => {
  const dispatch = useDispatch();

  // This is the bits of the state which can be stored in the URL.
  const hydrateStateQuery = useSelector(ProjectsV2Store.selectors.getHydrateStateQuery);
  // This action is bound with fetchProjects, meaning it will call the debounced
  // fetchProjects query after being dispatched.
  const hydrateState = useFetchProjectsAction(ProjectsV2Store.actions.hydrateState);

  // Updates the URL params when the state changes.
  // Debounced because we don't want to thrash the URL updates
  const syncURLParams = useDebouncedCallback((pathname: string, query: HydrateStateQuery) =>
    router.replace({
      pathname,
      search: `?${stringifyQuery(query)}`,
    }));

  /**
   * When the component mounts OR the user switches categories, we dispatch a hydrateState action.
   * This will sync the query params into the state (if they exist,
   * the reducer handles bad/empty values for us).
   *
   * Note that dispatching hydrateState also kicks off the initial
   * fetchProjects request (see: `useFetchProjectsAction`).
   */
  useEffect(() => {
    const parsedSearchParams = parseSearchParams(location?.search ?? '');
    // This is safe because only /projects and /release-groups will nav to this page.
    const category: ProjectCategory = _.camelCase(location.pathname?.split('/')?.[1]?.split('?')[0]) as ProjectCategory;
    hydrateState({
      ...parsedSearchParams,
      // We also want changes to the category to be reflected in the state, so we include this.
      category,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /**
   * Reset redux state when URL params get cleared. This happens when a user clicks the
   * FOSSA logo or the Projects nav link while already on the projects page.
   */
  useEffect(() => {
    // Only continue if the URL params are empty. Otherwise, every update to a
    // filter / sort / page option will result in a double load.
    if (location.search) {
      return;
    }

    // Reset the redux store to an empty state.
    hydrateState({});

    // Reset selected items
    dispatch(ProjectsV2Store.actions.clearSelection());
  }, [location.search]);

  /**
   * Effect which runs only when the state changes,
   * This is responsible for updating the URL params based on the updated Redux state.
   * Note that we are only looking at a subset of the state which would
   * actually go into the query params (see `HydrateStateQuery`).
   */
  useEffect(() => {
    syncURLParams(location.pathname, hydrateStateQuery);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We are intentionally ignoring location, as we want this to run only when the state is updated.
  }, [hydrateStateQuery]);
};

export default useSyncStateAndURLParams;
